import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ScrollableList } from '../ScrollableListComponent';
import { pageSize } from '../constants';
import {
    formatConditionIndexOverviewRequest,
    sortBasedOnOveralCondition
} from '../helpers';
import ExpandableComponent from './ExpandableComponent';
import PowertrainOverviewTitle from './PowertrainOverviewTitle';
import usePagination from 'sharedHooks/usePagination';
import { apiCallStatus } from 'helpers/constants';
import { useSearchExpansion } from '../hooks/useSearchExpansion';
import { setPowertrainSyncFlag } from '../../../GeneralFilter/GeneralFilterModal/actions';

const PowertrainOverviewComponent = (props) => {
    const { t: translate } = useTranslation();
    const [selectedPowertrain, setSelectedPowertrain] = useState(null);
    // const [selectedAsset, setSelectedAsset] = useState({});
    const [showAsset, setShowAsset] = useState(false);
    const [isHorizontalScrollActive, setIsHorizontalScrollActive] = useState(false);
    const [isLabelVisible, setIsLabelVisible] = useState(true);
    const {
        pageNumber,
        powertrainOverviewList,
        powertrainOverviewLoadingStatus,
        hasMore,
        showThreshold,
        showTrend,
        initialLoadDone,
        selectedAsset,
        overviewAssetList,
        refeshCIAMSync,
        refreshPowertrains
    } = props.model;
    const { getPowertrainConditionIndicesRequest, setSelectedAssetAction, previousSearchFilter } = props.actions;
    const {
        assetNumber,
        selectedFilters,
        searchBoxFilter,
        sensorIdentifierFilter,
        conditionFilter,
        assetTypeFilter,
        subscriptionTypeFilter,
        withVisibleFilter,
        isFilterInProgress,
        allAssets,
        leftPanelSize,
        sites
    } = props;
    const itemsPerPage = 50; // Number of items to load per page
    const filteredSelectedFilters = useMemo(() => {
        // nothing to do if there is no country selection or no sites available
        if (!selectedFilters || !Array.isArray(selectedFilters.countries) || selectedFilters?.countries?.length === 0 || !Array.isArray(sites) || sites?.length === 0) {
            return selectedFilters;
        }

        // If sites are selected, use only those sites (ignore country filter)
        if (Array.isArray(selectedFilters?.sites) && selectedFilters.sites.length > 0) {
            return {
                ...selectedFilters,
                countries: [],
                sites: selectedFilters.sites,
                sitesChecked: true
            };
        }

        // helper to extract country code / id from selected country entry
        const getCountryCodeFromCountry = (country) => {
            return country?.countryCode ?? country?.code ?? country?.countryCode ?? country;
        };

        // build list of selected country codes (normalized)
        const selectedCountryCodes = selectedFilters?.countries
            .map(getCountryCodeFromCountry)
            .filter(Boolean);

        if (selectedCountryCodes?.length === 0) {
            return selectedFilters;
        }

        // helper to get country code from site and site id
        const getSiteCountryCode = (site) => {
            return site?.countryCode ?? site?.country?.countryCode ?? site?.country ?? site?.country_code;
        };
        const getSiteId = (site) => site?.siteID ?? site?.siteId ?? site?.SiteId ?? site?.id;

        // filter sites that belong to selected countries
        const matchingSites = (sites || []).filter(site => {
            const siteCountry = getSiteCountryCode(site);
            return siteCountry && selectedCountryCodes.includes(siteCountry);
        }).map(site => {
            const sid = getSiteId(site);
            return { ...site, siteID: sid };
        }).filter(s => s.siteID !== null);

        return {
            ...selectedFilters,
            countries: [],
            sites: matchingSites,
            sitesChecked: matchingSites?.length > 0
        };
    }, [selectedFilters, sites]);
    const memoizedPowertrainOverviewList = useMemo(() => powertrainOverviewList, [powertrainOverviewList]);
    const memoizedFilters = useMemo(() => ({
        selectedFilters: filteredSelectedFilters,
        conditionFilter,
        assetTypeFilter,
        subscriptionTypeFilter,
        searchBoxFilter,
        sensorIdentifierFilter
    }), [filteredSelectedFilters, conditionFilter, assetTypeFilter, subscriptionTypeFilter, searchBoxFilter, sensorIdentifierFilter]);
    const { paginatedItems, loadMoreItems } = usePagination(memoizedPowertrainOverviewList, itemsPerPage);
    const timerRef = useRef(null);
    const sortedPowertrains = sortBasedOnOveralCondition(paginatedItems);
    const dispatch = useDispatch();

    useEffect(() => {
        if (powertrainOverviewLoadingStatus !== apiCallStatus.LOADING) {
            if (isFilterInProgress || !initialLoadDone || refeshCIAMSync || refreshPowertrains) {
                if (timerRef?.current) {
                    clearTimeout(timerRef?.current);
                }
                timerRef.current = setTimeout(() => {
                    getPowertrainConditionIndicesRequest(
                        formatConditionIndexOverviewRequest(
                            1,
                            memoizedFilters?.selectedFilters,
                            memoizedFilters?.searchBoxFilter,
                            memoizedFilters?.conditionFilter,
                            memoizedFilters?.assetTypeFilter,
                            memoizedFilters?.subscriptionTypeFilter,
                            memoizedFilters?.sensorIdentifierFilter,
                            refreshPowertrains
                        )
                    );
                    dispatch(setPowertrainSyncFlag());
                    previousSearchFilter(searchBoxFilter);
                    return () => {
                        if (timerRef?.current) {
                            clearTimeout(timerRef?.current);
                        }
                    };
                }, memoizedFilters?.searchBoxFilter?.length > 0 ? 700 : 0);
            }
        }
    }, [memoizedFilters, isFilterInProgress, initialLoadDone, powertrainOverviewLoadingStatus, refeshCIAMSync]);

    const handleSelectedItem = (asset) => {
        setSelectedAssetAction(asset, overviewAssetList);
    };

    const handleReset = () => {
        setSelectedAssetAction(null);
    };

    const handleAssetToggle = useCallback(() => setShowAsset(prevShowAsset => !prevShowAsset), []);

    const handlePowertrainClick = (powertrain, index, toggleSize) => {
        if (selectedPowertrain === powertrain?.powertrainId) {
            setSelectedPowertrain(null);
        } else {
            setSelectedPowertrain(powertrain?.powertrainId);
        }
        const assetCount = powertrain?.assets?.length + 1 || 1; // Default to 1 if no assets
        toggleSize(index, assetCount);
    };

    // Add search expansion functionality
    useSearchExpansion(
        searchBoxFilter,
        memoizedPowertrainOverviewList,
        selectedPowertrain,
        setSelectedPowertrain,
        handleSelectedItem,
        selectedAsset,
        sensorIdentifierFilter,
        powertrainOverviewLoadingStatus
    );

    const onHorizontalScrollHandler = (isScroll) => {
        setIsHorizontalScrollActive(isScroll);
    };

    const onLabelVisibilityHandler = (isVisibe) => {
        setIsLabelVisible(isVisibe);
    };

    const onLoadMore = () => {
        if (paginatedItems?.length < memoizedPowertrainOverviewList?.length) {
            loadMoreItems();
        } else if (hasMore) {
            getPowertrainConditionIndicesRequest(formatConditionIndexOverviewRequest(
                pageNumber,
                filteredSelectedFilters,
                searchBoxFilter,
                conditionFilter,
                assetTypeFilter,
                sensorIdentifierFilter
            ));
        }
    };

    const renderExpandableTitle = useCallback((item, index) => {
        return <PowertrainOverviewTitle
            index={index}
            item={item}
            isHorizontalScrollActive={isHorizontalScrollActive}
            showThreshold={showThreshold}
            showTrend={showTrend}
            leftPanelSize={leftPanelSize}
            selectedAsset={selectedAsset}
        />;
    }, [isHorizontalScrollActive, showThreshold, showTrend, leftPanelSize, selectedAsset]);

    const renderPowertrainOverView = ({ item, index, toggleSize }) => {
        return <ExpandableComponent
            key={item?.powertrainId}
            item={item}
            index={index}
            renderExpandableTitle={() => renderExpandableTitle(item, index)}
            handlePowertrainClick={() => handlePowertrainClick(item, index, toggleSize)}
            selectedPowertrain={selectedPowertrain}
            selectedAsset={selectedAsset}
            handleSelectedItem={handleSelectedItem}
            handleAssetToggle={handleAssetToggle}
            isHorizontalScrollActive={isHorizontalScrollActive}
            memoizedPowertrainOverviewList={memoizedPowertrainOverviewList}
            translate={translate}
            model={props.model}
            allAssets={allAssets}
            isLabelVisible={isLabelVisible}
            leftPanelSize={leftPanelSize}
        />;
    };

    return <ScrollableList
        role='scrollable-list'
        noDataLabel={translate('ABB.Powertrain.Frontend.conditionIndicesOverviewNoItems')}
        className={`asset overview ${withVisibleFilter ? 'with-filter' : ''}`}
        assetNumber={assetNumber}
        loadingStatus={powertrainOverviewLoadingStatus}
        hasMore={true}
        showThreshold={props?.model?.showThreshold}
        showTrend={props?.model?.showTrend}
        pageSize={pageSize}
        loadMore={onLoadMore}
        renderList={renderPowertrainOverView}
        selectedAsset={selectedAsset}
        hasData={sortedPowertrains}
        selectedPowertrain={selectedPowertrain}
        onReset={handleReset}
        showAsset={showAsset}
        onHorizontalScroll={onHorizontalScrollHandler}
        onLabelVisibilityChange={onLabelVisibilityHandler}
        leftPanelSize={leftPanelSize}
        noWrapper={false}
        listType='VariableSizeList'
    />;
};

PowertrainOverviewComponent.propTypes = {
    model: PropTypes.shape({
        pageNumber: PropTypes.number,
        powertrainOverviewLoadingStatus: PropTypes.string,
        powertrainOverviewList: PropTypes.array,
        hasMore: PropTypes.bool,
        initialLoadDone: PropTypes.bool,
        showThreshold: PropTypes.bool,
        showTrend: PropTypes.bool,
        refreshPowertrains: PropTypes.bool,
        refeshCIAMSync: PropTypes.bool
    }),
    actions: PropTypes.shape({
        getPowertrainConditionIndicesRequest: PropTypes.func,
        setSelectedAssetAction: PropTypes.func,
        previousSearchFilter: PropTypes.func,
        handleAssetToggle: PropTypes.func
    }),
    assetNumber: PropTypes.number,
    showThreshold: PropTypes.bool,
    showTrend: PropTypes.bool,
    subscriptionTypeFilter: PropTypes.array,
    powertrain: PropTypes.shape({
        powertrainId: PropTypes.number,
        selectedAsset: PropTypes.object
    }),
    allAssets: PropTypes.array,
    selectedNotification: PropTypes.shape({
        assetId: PropTypes.number
    }),
    selectedFilters: PropTypes.object,
    searchBoxFilter: PropTypes.string,
    sensorIdentifierFilter: PropTypes.string,
    conditionFilter: PropTypes.array,
    assetTypeFilter: PropTypes.string,
    withVisibleFilter: PropTypes.bool,
    isFilterInProgress: PropTypes.bool
};


export default PowertrainOverviewComponent;
